/**
 * Component handling product collection filtering, sorting, and dynamic AJAX section rendering.
 * @extends HTMLElement
 */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    this.initHorizontalFacets();
  }
  initHorizontalFacets() {
    if (this.classList.contains('facets__horizontal')) {
      this.setupHorizontalFacets();
    }
    this.horizontalObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          if (this.classList.contains('facets__horizontal')) {
            this.setupHorizontalFacets();
          }
        }
      });
    });
    this.querySelector('#FacetsWrapperDesktop') && this.horizontalObserver.observe(this.querySelector('#FacetsWrapperDesktop'), {
      childList: true,
      subtree: false
    });
  }
  setupHorizontalFacets() {
    document.removeEventListener('click', this.closeTab);
    this.querySelectorAll('.facets__disclosure__horizontal summary').forEach(item => {
      item.removeEventListener('click', this.openTab);
    });
    document.addEventListener('click', this.closeTab.bind(this));
    this.querySelectorAll('.facets__disclosure__horizontal summary').forEach((item) => {
      item.addEventListener('click', this.openTab.bind(this));
    });
  }
  openTab(event){
    var current = event.target.closest('details');
    if(current.classList.contains('details__opened')){
      current.classList.remove('details__opened');
    }else{
      setTimeout(function(){current.classList.add('details__opened');}, 100)
    }
  }
  closeTab(event){
    if(!this.classList.contains('facets__horizontal')) return;
    var index = -1;
    var current = event.target.closest('details');
    if(current){
      index = current.getAttribute('data-index');
    }
    this.querySelectorAll('.facets__disclosure__horizontal[open]').forEach((item) => {
      if(item.getAttribute('data-index') != index){
        item.removeAttribute('open');
        item.classList.remove('details__opened');
        item.querySelector('summary').setAttribute('aria-expanded', false);
      }
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  /**
   * Fetches and re-renders collection grid and filters based on search parameters.
   * @param {string} searchParams
   * @param {Event} [event]
   * @param {boolean} [updateURLHash=true]
   */
  static renderPage(searchParams, event, updateURLHash = true) {
    if (document.querySelector('.rr-shoe-nav')) {
      const params = new URLSearchParams(searchParams);
      if (params.has('filter.v.option.numero')) params.set('filter.v.availability', '1');
      else params.delete('filter.v.availability');
      searchParams = params.toString();
    }
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();

    placeCollectionLoader();
    document.getElementById('ProductGridContainer').classList.add('loading_now');
    document.querySelector('.collection_facets_loader').classList.add('loading_now');
    
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateURLHash)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateURLHash);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, resetScroll = true) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.removePreloader();
        if (resetScroll) FacetFiltersForm.scrollToResultsStart();
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, resetScroll = true) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);    
    FacetFiltersForm.removePreloader();
    if (resetScroll) FacetFiltersForm.scrollToResultsStart();
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static scrollToResultsStart() {
    if (!window.location.pathname.includes('/collections/')) return;
    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const drawer = mobileForm && mobileForm.closest('menu-drawer');
    const isDrawerOpen = drawer && drawer.querySelector('details[open]');
    if (isDrawerOpen && document.body.hasAttribute('data-top')) {
      document.body.setAttribute('data-top', '0');
      document.body.style.top = '0px';
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }

  static removePreloader(){
    window.dispatchEvent(new CustomEvent('updateGrid'));
    document.querySelector('.collection_facets_loader').classList.remove('loading_now');
    document.getElementById('ProductGridContainer').classList.remove('loading_now');
  }
  
  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const countContainer = document.getElementById('ProductCount');
    if(!countContainer) return false;
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    container.innerHTML = count;
  }

  /**
   * Updates active filter UI elements and details from fetched HTML.
   * @param {string} html
   * @param {Event} [event]
   */
  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    var removed = false;
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.closest('details').remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (elementToRender.parentElement) {
          var _parent_current = document.querySelector(`#${elementToRender.parentElement.id}`);
        }
        if (index > 0 && elementToRender.parentElement && _parent_current) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          var _index = elementToRender.parentElement.dataset.index;
          var _fl = document.querySelector(`#${elementToRender.parentElement.id}`);
          if(_fl){
            if(_fl.querySelector(`.js-filter`)){
              _fl.querySelector(`.js-filter`).before(elementToRender);
            }
          }
          else{
            if(elementToRender.parentElement.classList.contains('mobile-facets__details')){
              var parent = document.querySelector(`#FacetFiltersFormMobile .facet-drawer__content__scroll`);
            }
            else{
              var parent = document.querySelector(`#FacetsWrapperDesktop`);
            }
            parent.insertBefore(elementToRender.parentElement, parent.children[_index])
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus({ preventScroll: true });
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '.rr-shoe-nav'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile')&&document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    if (document.querySelector('.rr-shoe-nav')) {
      const params = new URLSearchParams(searchParams);
      const current = new URLSearchParams(window.location.search);
      current.getAll('filter.p.product_type').forEach(value => {
        if (!params.getAll('filter.p.product_type').includes(value)) params.append('filter.p.product_type', value);
      });
      if (params.has('filter.v.option.numero')) params.set('filter.v.availability', '1');
      else params.delete('filter.v.availability');
      searchParams = params.toString();
    }
    FacetFiltersForm.renderPage(searchParams, event);
  }

  /** Handles form submission and aggregates active filter parameters. */
  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    if (document.querySelector(".rr-line-filter")) {
      const destination = new URL(event.currentTarget.href);
      if (!destination.searchParams.has("filter.p.vendor")) {
        destination.pathname = destination.pathname.split("/linea-")[0];
      }
      window.location.assign(destination.pathname + destination.search);
      return;
    }
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/**
 * Price range filter input component enforcing min/max boundaries.
 * @extends HTMLElement
 */
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  /**
   * Clamps input value within allowed data-min and data-max bounds.
   * @param {HTMLInputElement} input
   */
  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

/**
 * Element trigger for removing active filter tags.
 * @extends HTMLElement
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
if (!customElements.get('rr-weight-range')) {
  customElements.define('rr-weight-range', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.values = JSON.parse(this.dataset.values).map(Number).filter(Number.isFinite);
      if (!this.values.length) return;
      this.floor = Math.min(...this.values);
      this.ceiling = Math.max(...this.values);
      const selected = JSON.parse(this.dataset.active).map(Number).filter(Number.isFinite);
      this.low = selected.length ? Math.max(this.floor, Math.min(...selected)) : this.floor;
      this.high = selected.length ? Math.min(this.ceiling, Math.max(...selected)) : this.ceiling;
      const current = new URLSearchParams(window.location.search);
      if (current.has(this.dataset.param)) {
        const low = Number(current.get((this.dataset.unit === 'mm' ? 'rr_balance_min' : 'rr_weight_min')));
        const high = Number(current.get((this.dataset.unit === 'mm' ? 'rr_balance_max' : 'rr_weight_max')));
        if (current.has((this.dataset.unit === 'mm' ? 'rr_balance_min' : 'rr_weight_min')) && current.has((this.dataset.unit === 'mm' ? 'rr_balance_max' : 'rr_weight_max')) && Number.isFinite(low) && Number.isFinite(high) && low <= high) {
          this.low = Math.max(this.floor, low);
          this.high = Math.min(this.ceiling, high);
        }
      }
      this.inputs = Array.from(this.querySelectorAll('input'));
      this.inputs.forEach(input => {
        input.min = this.floor;
        input.max = this.ceiling;
      });
      this.addEventListener('input', event => {
        event.stopPropagation();
        const input = event.target;
        if (!input.matches('input[data-bound]') || input.value === '') return;
        this.update(input);
      });
      this.addEventListener('change', event => {
        event.stopPropagation();
        if (!event.target.matches('input[data-bound]')) return;
        this.update(event.target);
        this.paint();
        if (event.target.type === 'range') this.apply();
      });
      this.addEventListener('keydown', event => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          this.update(event.target);
          this.apply();
        }
      });
      this.querySelector('[data-apply]').addEventListener('click', () => this.apply());
      this.querySelector('[data-reset]').addEventListener('click', () => {
        this.low = this.floor;
        this.high = this.ceiling;
        this.paint();
        this.apply(true);
      });
      this.paint();
      this.hidden = false;
      const nativeList = this.nextElementSibling;
      if (nativeList && nativeList.tagName === 'UL') nativeList.hidden = true;
    }
    update(input) {
      if (!input.dataset.bound) return;
      const parsed = Number(input.value);
      const value = Math.round(Math.max(this.floor, Math.min(this.ceiling, Number.isFinite(parsed) ? parsed : this.floor)));
      if (input.dataset.bound === 'min') this.low = Math.min(value, this.high);
      else this.high = Math.max(value, this.low);
      this.paint(input.type === 'number' ? input : null);
    }
    paint(editing) {
      this.inputs.forEach(input => {
        if (input !== editing) input.value = input.dataset.bound === 'min' ? this.low : this.high;
        if (input.type === 'range') {
          input.setAttribute('aria-valuetext', input.value + ' ' + (this.dataset.unit || 'g'));
        }
      });
      this.querySelector('.rr-weight-summary').textContent = this.dataset.summary.replace('[min]', this.low).replace('[max]', this.high);
      const span = this.ceiling - this.floor || 1;
      this.style.setProperty('--rr-low', ((this.low - this.floor) / span * 100) + '%');
      this.style.setProperty('--rr-high', ((this.high - this.floor) / span * 100) + '%');
      this.querySelector('input[type="range"][data-bound="min"]').style.zIndex = this.low === this.ceiling ? '4' : '2';
    }
    apply(reset = false) {
      const url = new URL(window.location.href);
      url.searchParams.delete(this.dataset.param);
      url.searchParams.delete('page');
      url.searchParams.delete((this.dataset.unit === 'mm' ? 'rr_balance_min' : 'rr_weight_min'));
      url.searchParams.delete((this.dataset.unit === 'mm' ? 'rr_balance_max' : 'rr_weight_max'));
      if (!reset && (this.low !== this.floor || this.high !== this.ceiling)) {
        url.searchParams.set((this.dataset.unit === 'mm' ? 'rr_balance_min' : 'rr_weight_min'), String(this.low));
        url.searchParams.set((this.dataset.unit === 'mm' ? 'rr_balance_max' : 'rr_weight_max'), String(this.high));
        const matches = this.values.filter(value => value >= this.low && value <= this.high);
        (matches.length ? matches : [-1]).forEach(value => url.searchParams.append(this.dataset.param, String(value)));
      }
      window.location.assign(url.pathname + url.search + url.hash);
    }
  });
}

if (!window.rrLineNavigationBound) {
  window.rrLineNavigationBound = true;
  document.addEventListener('click', event => {
    const link = event.target.closest('a[data-rr-line-link]');
    if (!link || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const destination = new URL(link.href);
    const current = new URL(window.location.href);
    for (const name of ['preview_theme_id','rr_weight_min','rr_weight_max','rr_balance_min','rr_balance_max']) {
      if (current.searchParams.has(name)) destination.searchParams.set(name,current.searchParams.get(name));
    }
    event.preventDefault();
    window.location.assign(destination.pathname + destination.search);
  });
}
